import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  createTicketUploadJob,
  fetchTicketEvents,
  fetchTicketJobs,
  selectTicketUploadsCreateStatus,
  selectTicketUploadsEvents,
} from "../store/slices/ticketUploadsSlice";
import { selectAccessToken } from "../store/slices/authSlice";
import { selectEventItems } from "../store/slices/eventsSlice";
import { forceLogoutToLogin, isUnauthorizedApiResponse } from "../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const MAX_UPLOAD_MB = Number(process.env.REACT_APP_TICKET_UPLOAD_MAX_MB || 5);
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

const TicketUploadCreatePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const events = useSelector(selectTicketUploadsEvents);
  const fallbackEvents = useSelector(selectEventItems);
  const createStatus = useSelector(selectTicketUploadsCreateStatus);
  const accessToken = useSelector(selectAccessToken);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [file, setFile] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchTicketEvents());
  }, [dispatch]);

  const handleFileChange = (event) => {
    const nextFile = event.target.files[0] || null;
    if (nextFile && nextFile.size > MAX_UPLOAD_BYTES) {
      setLocalError(`File exceeds ${MAX_UPLOAD_MB} MB limit. Please split the file.`);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    setLocalError(null);
    setFile(nextFile);
  };

  const handleDownloadSample = async () => {
    setDownloadError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/ticket-upload-jobs/template-file`,
        {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        }
      );
      if (isUnauthorizedApiResponse(response)) {
        forceLogoutToLogin();
        return;
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (isUnauthorizedApiResponse(response, payload)) {
          forceLogoutToLogin();
          return;
        }
        throw new Error(payload?.resultDesc || "Unable to download sample file.");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ticket-upload-sample.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err.message);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLocalError(null);
    if (!selectedEvent) {
      setLocalError("Please select an event.");
      return;
    }
    if (!file) {
      setLocalError("Please upload an Excel file.");
      return;
    }
    try {
      await dispatch(
        createTicketUploadJob({ eventId: selectedEvent, file })
      ).unwrap();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setFile(null);
      setSelectedEvent("");
      dispatch(fetchTicketJobs());
      navigate("/ticket-uploads");
    } catch (err) {
      setLocalError(err || "Failed to upload ticket batch.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setFile(null);
    }
  };

  const handleCancel = () => {
    navigate("/ticket-uploads");
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Typography variant="h4" fontWeight={700}>
          Upload Ticket Batch
        </Typography>
        <Typography color="text.secondary">
          Select the target event and upload the prepared Excel file.
        </Typography>
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          {createStatus === "loading" && (
            <LinearProgress sx={{ mb: 2 }} color="primary" />
          )}
          {(localError || downloadError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {localError || downloadError}
            </Alert>
          )}
          <Stack spacing={3}>
            <TextField
              select
              label="Event"
              fullWidth
              value={selectedEvent}
              onChange={(event) => setSelectedEvent(event.target.value)}
            >
              <MenuItem value="">Select event</MenuItem>
              {(events.length ? events : fallbackEvents).map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.name}
                </MenuItem>
              ))}
            </TextField>
            <Stack spacing={1}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
              >
                {file ? file.name : "Choose Excel File"}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                Accepted formats: .xls, .xlsx · Max size {MAX_UPLOAD_MB} MB · Max 100,000 rows · Must include a "barcode" column.
              </Typography>
            </Stack>
            <Button
              variant="text"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSample}
              sx={{ alignSelf: "flex-start" }}
            >
              Download sample template
            </Button>
          </Stack>
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ mt: 4 }}
          >
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createStatus === "loading"}
            >
              {createStatus === "loading" ? "Uploading..." : "Submit"}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default TicketUploadCreatePage;
